/**
 * Small runtime polyfills for older browsers (mainly Chrome/Android WebView
 * 80–91). Vite only transpiles syntax, it does not add missing built-ins, so
 * these are needed for APIs used by our code and dependencies:
 *  - Array/String.prototype.at — web-vitals (CLS) calls `.at(-1)`, which
 *    threw "this.o.at is not a function" in Datadog.
 *  - AbortSignal.timeout — used by the maintenance health check; without it
 *    the check fails on old browsers and the API looks down.
 * Imported first in main.tsx so it runs before any other module code.
 */

function at<T>(this: ArrayLike<T>, index: number): T | undefined {
  const length = this.length;
  let i = Math.trunc(index) || 0;
  if (i < 0) i += length;
  if (i < 0 || i >= length) return undefined;
  return this[i];
}

const defineAt = (proto: object) => {
  if (!("at" in proto)) {
    Object.defineProperty(proto, "at", {
      value: at,
      writable: true,
      configurable: true,
    });
  }
};

defineAt(Array.prototype);
defineAt(String.prototype);

if (typeof AbortSignal !== "undefined" && !("timeout" in AbortSignal)) {
  Object.defineProperty(AbortSignal, "timeout", {
    value: (ms: number): AbortSignal => {
      const controller = new AbortController();
      setTimeout(
        () =>
          controller.abort(
            new DOMException("signal timed out", "TimeoutError")
          ),
        ms
      );
      return controller.signal;
    },
    writable: true,
    configurable: true,
  });
}

export {};
